import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { menuAPI, settingsAPI } from '../utils/api';
import { setSEO } from '../utils/seo';
import LocationPopup from '../components/LocationPopup';
import { fuzzySearchItems, buildSuggestions, highlightMatch } from '../utils/fuzzySearch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_OPTIONS = [
  { code: 'en', label: 'English', flag: 'GB' },
  { code: 'vi', label: 'Tiếng Việt', flag: 'VN' },
  { code: 'ru', label: 'Русский', flag: 'RU' },
  { code: 'fr', label: 'Français', flag: 'FR' },
  { code: 'ja', label: '日本語', flag: 'JP' },
  { code: 'ko', label: '한국어', flag: 'KR' },
  { code: 'zh', label: '中文', flag: 'CN' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
  { code: 'es', label: 'Español', flag: 'ES' },
  { code: 'th', label: 'ไทย', flag: 'TH' },
];

const GOOGLE_MAPS_URL = 'https://www.google.com/maps/search/Benz+Eatery+Phu+Quoc+Vietnam';

const PublicMenu = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState('en');
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showLang, setShowLang] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [settings, setSettings] = useState(null);
  const searchWrapRef = useRef(null);

  useEffect(() => {
    setSEO({
      title: 'Menu | Benz Eatery Phu Quoc | Vietnamese & International Cuisine',
      description: 'Explore Benz Eatery full menu — 230+ dishes including fresh seafood, pho, Vietnamese specialties, cocktails, pasta & more. Best food on Phu Quoc Island, Vietnam.',
      ogType: 'restaurant.menu',
      canonical: 'https://benzeatery.com/menu',
    });
    Promise.all([
      menuAPI.getAll(),
      settingsAPI.get(),
    ]).then(([menuRes, settingsRes]) => {
      setItems(menuRes.data);
      setSettings(settingsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const getName = (item) => {
    const n = item.name;
    if (typeof n === 'string') return n;
    return n?.[lang] || n?.en || 'Menu Item';
  };

  const categories = useMemo(() => {
    const cats = [...new Set(items.map(i => i.category || 'Other'))];
    return ['All', ...cats.sort()];
  }, [items]);

  // Top 5 categories by item count, used as quick-browse chips in the
  // suggestions dropdown when the user focuses the empty search field.
  const popularCategories = useMemo(() => {
    const counts = {};
    items.forEach(i => {
      const c = i.category || 'Other';
      counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({ name, count }));
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim();
    // When searching, ignore category filter so users can find any dish by
    // typing (typos allowed). When not searching, apply category filter.
    if (q) {
      const scored = fuzzySearchItems(items, q, getName);
      return scored.map(s => s.item);
    }
    if (activeCategory !== 'All') return items.filter(i => i.category === activeCategory);
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, activeCategory, search, lang]);

  // Top matches for the suggestion dropdown while user is typing.
  const searchSuggestions = useMemo(() => {
    const q = search.trim();
    if (!q || q.length < 1) return [];
    const scored = fuzzySearchItems(items, q, getName);
    return scored.slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, lang]);

  // Typo-correction "Did you mean" — shown when we have zero / very few direct hits.
  const didYouMean = useMemo(() => {
    const q = search.trim();
    if (!q || q.length < 3) return [];
    if (searchSuggestions.length >= 3) return [];
    return buildSuggestions(items, q, getName, 3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, lang, searchSuggestions.length]);

  // Close suggestion panel on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(i => {
      const c = i.category || 'Other';
      if (!g[c]) g[c] = [];
      g[c].push(i);
    });
    return g;
  }, [filtered]);

  const currentLang = LANG_OPTIONS.find(l => l.code === lang) || LANG_OPTIONS[0];

  const renderStars = (rating) => {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.3;
    return (
      <span className="pm-stars">
        {Array.from({ length: 5 }, (_, i) => (
          <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={i < full ? '#FFB800' : (i === full && half ? 'url(#half)' : '#ddd')} stroke="none">
            <defs><linearGradient id="half"><stop offset="50%" stopColor="#FFB800"/><stop offset="50%" stopColor="#ddd"/></linearGradient></defs>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        ))}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="pm-loading" data-testid="public-menu-loading">
        <div className="pm-spinner" />
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      </div>
    );
  }

  return (
    <div className="pm-root" data-testid="public-menu-page">
      {/* Header */}
      <header className="pm-header">
        <div className="pm-header-inner">
          <Link to="/" className="pm-logo" data-testid="pm-home-link">
            {settings?.restaurant_logo ? (
              <img
                src={settings.restaurant_logo}
                alt={settings.restaurant_name || 'Benz Eatery'}
                className="pm-logo-img"
                data-testid="pm-logo-img"
              />
            ) : (
              <div className="pm-logo-mark"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg></div>
            )}
            <div>
              <span className="pm-logo-name">{settings?.restaurant_name || 'Benz Eatery'}</span>
              <span className="pm-logo-sub">{settings?.restaurant_tagline || 'Phu Quoc Premium Dining'}</span>
            </div>
          </Link>
          <div className="pm-header-right">
            <button
              className="pm-lang-btn"
              onClick={() => setShowLang(!showLang)}
              data-testid="pm-lang-toggle"
            >
              <span>{currentLang.flag}</span>
              <span className="pm-lang-name">{currentLang.label}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="pm-hero" data-testid="pm-hero">
        <h1>Our Menu</h1>
        <p>Fresh Vietnamese cuisine & international flavors — crafted daily on Phu Quoc Island</p>
      </div>

      {/* Reviews Banner */}
      {settings && (
        <div className="pm-reviews-bar" data-testid="pm-reviews-bar">
          <a href={settings.google_review_url || GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="pm-review-badge" data-testid="pm-google-review">
            <svg viewBox="0 0 24 24" width="20" height="20"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            <div className="pm-review-info">
              <div className="pm-review-stars">
                {renderStars(settings.google_rating || 4.8)}
                <span className="pm-review-rating">{settings.google_rating || 4.8}</span>
              </div>
              <span className="pm-review-count">{settings.google_review_count || 150} reviews</span>
            </div>
          </a>
          <div className="pm-review-sep" />
          <a href={settings.tripadvisor_review_url || '#'} target="_blank" rel="noopener noreferrer" className="pm-review-badge" data-testid="pm-tripadvisor-review">
            <svg viewBox="0 0 24 24" width="22" height="22"><circle cx="12" cy="12" r="11" fill="#00AF87"/><path d="M12 6.5c-2.5 0-4.7 1.3-6 3.2h1.8a4.3 4.3 0 018.4 0h1.8c-1.3-1.9-3.5-3.2-6-3.2z" fill="white"/><circle cx="8.5" cy="13" r="2.8" fill="none" stroke="white" strokeWidth="1.3"/><circle cx="8.5" cy="13" r="1" fill="white"/><circle cx="15.5" cy="13" r="2.8" fill="none" stroke="white" strokeWidth="1.3"/><circle cx="15.5" cy="13" r="1" fill="white"/></svg>
            <div className="pm-review-info">
              <div className="pm-review-stars">
                {renderStars(settings.tripadvisor_rating || 4.7)}
                <span className="pm-review-rating">{settings.tripadvisor_rating || 4.7}</span>
              </div>
              <span className="pm-review-count">{settings.tripadvisor_review_count || 216} reviews</span>
            </div>
          </a>
        </div>
      )}

      {/* Language selector dropdown */}
      {showLang && (
        <div className="pm-lang-dropdown" data-testid="pm-lang-dropdown">
          {LANG_OPTIONS.map(l => (
            <button
              key={l.code}
              className={`pm-lang-option ${lang === l.code ? 'active' : ''}`}
              onClick={() => { setLang(l.code); setShowLang(false); }}
              data-testid={`pm-lang-${l.code}`}
            >
              <span>{l.flag}</span> {l.label}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="pm-search-wrap" ref={searchWrapRef}>
        <input
          className="pm-search"
          type="text"
          placeholder="Search dishes, try 'pho', 'seafood', 'pizza'..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          data-testid="pm-search"
          autoComplete="off"
        />
        <svg className="pm-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        {search && (
          <button
            type="button"
            className="pm-search-clear"
            onClick={() => setSearch('')}
            aria-label="Clear search"
            data-testid="pm-search-clear"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        )}

        {/* Suggestions panel */}
        {searchFocused && (
          <div className="pm-suggest" data-testid="pm-suggest">
            {/* Empty query: show popular categories as quick-browse chips */}
            {!search.trim() && (
              <div className="pm-suggest-section">
                <div className="pm-suggest-title">Popular categories</div>
                <div className="pm-suggest-chips">
                  {popularCategories.map(cat => (
                    <button
                      key={cat.name}
                      type="button"
                      className="pm-suggest-chip"
                      onClick={() => {
                        setActiveCategory(cat.name);
                        setSearchFocused(false);
                      }}
                      data-testid={`pm-suggest-cat-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {cat.name} <span className="pm-suggest-chip-count">{cat.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Typing: show top matching dishes with highlights */}
            {search.trim() && searchSuggestions.length > 0 && (
              <div className="pm-suggest-section">
                <div className="pm-suggest-title">Suggestions</div>
                <ul className="pm-suggest-list" data-testid="pm-suggest-list">
                  {searchSuggestions.map(({ item }) => {
                    const parts = highlightMatch(getName(item), search);
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          className="pm-suggest-item"
                          onClick={() => {
                            setSelectedDish(item);
                            setSearchFocused(false);
                          }}
                          data-testid={`pm-suggest-item-${item.id}`}
                        >
                          <span className="pm-suggest-item-icon" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                          </span>
                          <span className="pm-suggest-item-text">
                            {parts.map((p, idx) =>
                              p.match
                                ? <mark key={idx}>{p.text}</mark>
                                : <span key={idx}>{p.text}</span>
                            )}
                          </span>
                          <span className="pm-suggest-item-cat">{item.category}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Did-you-mean block */}
            {search.trim() && didYouMean.length > 0 && (
              <div className="pm-suggest-section pm-suggest-dym" data-testid="pm-did-you-mean">
                <div className="pm-suggest-title">Did you mean</div>
                <div className="pm-suggest-chips">
                  {didYouMean.map((name) => (
                    <button
                      key={name}
                      type="button"
                      className="pm-suggest-chip pm-suggest-chip-dym"
                      onClick={() => setSearch(name)}
                      data-testid="pm-dym-chip"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No results at all */}
            {search.trim() && searchSuggestions.length === 0 && didYouMean.length === 0 && (
              <div className="pm-suggest-empty" data-testid="pm-suggest-empty">
                No matches for "<strong>{search}</strong>". Try a category below.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Categories */}
      <div className="pm-cats-wrap">
        <div className="pm-cats" data-testid="pm-categories">
          {categories.map(cat => (
            <button
              key={cat}
              className={`pm-cat ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
              data-testid={`pm-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Menu items */}
      <main className="pm-main">
        {Object.entries(grouped).map(([category, catItems]) => (
          <section key={category} className="pm-section">
            <h2 className="pm-section-title" data-testid={`pm-section-${category.toLowerCase().replace(/\s+/g, '-')}`}>
              {category}
              <span className="pm-section-count">{catItems.length}</span>
            </h2>
            <div className="pm-grid">
              {catItems.map(item => (
                <div className="pm-card" key={item.id} onClick={() => setSelectedDish(item)} data-testid={`pm-item-${item.id}`}>
                  {item.image_url ? (
                    <div className="pm-card-img">
                      <img
                        src={item.image_url.startsWith('/') ? `${API_URL}${item.image_url}` : item.image_url}
                        alt={getName(item)}
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="pm-card-img pm-card-img-placeholder">
                      <span>{getName(item).charAt(0)}</span>
                    </div>
                  )}
                  <div className="pm-card-body">
                    <h3>{getName(item)}</h3>
                    {item.description && <p>{item.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
        {Object.keys(grouped).length === 0 && (
          <div className="pm-empty" data-testid="pm-empty-results">
            {search.trim() ? (
              <>
                <div className="pm-empty-title">No dishes found for "<strong>{search}</strong>"</div>
                {didYouMean.length > 0 && (
                  <div className="pm-empty-suggest">
                    <span className="pm-empty-suggest-label">Did you mean:</span>
                    {didYouMean.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="pm-suggest-chip pm-suggest-chip-dym"
                        onClick={() => setSearch(name)}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="pm-empty-clear"
                  onClick={() => { setSearch(''); setActiveCategory('All'); }}
                  data-testid="pm-empty-clear"
                >
                  Clear search
                </button>
              </>
            ) : (
              'No dishes found.'
            )}
          </div>
        )}
      </main>

      {/* Dish Detail Modal */}
      {selectedDish && (
        <div className="pm-modal-overlay" onClick={() => setSelectedDish(null)} data-testid="dish-modal">
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pm-modal-close" onClick={() => setSelectedDish(null)} data-testid="dish-modal-close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
            {selectedDish.image_url ? (
              <div className="pm-modal-img">
                <img src={selectedDish.image_url.startsWith('/') ? `${API_URL}${selectedDish.image_url}` : selectedDish.image_url} alt={getName(selectedDish)} />
              </div>
            ) : (
              <div className="pm-modal-img pm-modal-img-ph">
                <span>{getName(selectedDish).charAt(0)}</span>
              </div>
            )}
            <div className="pm-modal-body">
              <span className="pm-modal-cat">{selectedDish.category}</span>
              <h2 className="pm-modal-name">{getName(selectedDish)}</h2>
              {selectedDish.description && <p className="pm-modal-desc">{selectedDish.description}</p>}

              {/* Ingredients */}
              {selectedDish.ingredients && (
                <div className="pm-modal-section" data-testid="dish-ingredients">
                  <h4 className="pm-modal-label">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2a10 10 0 0 1 10 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2m0 4v4l3 3"/></svg>
                    Ingredients
                  </h4>
                  <p className="pm-modal-ingredients">{selectedDish.ingredients}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pm-modal-actions">
                <a
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pm-modal-btn pm-modal-btn-nav"
                  data-testid="dish-navigate-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  Navigate to Restaurant
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <LocationPopup lang={lang} />

      <footer className="pm-footer">
        <p>Benz Eatery — Phu Quoc Island, Vietnam</p>
        <p className="pm-footer-rcs">
          Dev by <a href="https://rigeneconsultancy.com" target="_blank" rel="noopener noreferrer">RCS</a>
        </p>
      </footer>

      <style dangerouslySetInnerHTML={{ __html: STYLES }} />
    </div>
  );
};

const STYLES = `
  .pm-root {
    --bg: #faf8f5;
    --surface: #ffffff;
    --text: #1c1a17;
    --text-muted: #6b6660;
    --text-soft: #9a9590;
    --border: rgba(0,0,0,0.08);
    --accent: #da251d;
    --accent-2: #ffcd00;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
  }
  .pm-root a { color: inherit; text-decoration: none; }
  .pm-loading {
    min-height: 100vh; display: flex; align-items: center; justify-content: center;
    background: #faf8f5;
  }
  .pm-spinner {
    width: 28px; height: 28px; border: 2px solid #da251d; border-top-color: transparent;
    border-radius: 50%; animation: pm-spin 0.7s linear infinite;
  }
  @keyframes pm-spin { to { transform: rotate(360deg); } }

  .pm-header {
    position: sticky; top: 0; z-index: 40;
    background: rgba(250,248,245,0.92);
    backdrop-filter: blur(12px);
    border-bottom: 0.5px solid var(--border);
  }
  .pm-header-inner {
    max-width: 960px; margin: 0 auto;
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 20px;
  }
  .pm-logo {
    display: flex; align-items: center; gap: 10px;
  }
  .pm-logo-mark {
    width: 32px; height: 32px; background: var(--accent); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: var(--accent-2); font-size: 16px; flex-shrink: 0;
  }
  .pm-logo-img {
    width: 40px; height: 40px; border-radius: 10px;
    object-fit: cover; background: #fff; flex-shrink: 0;
    border: 0.5px solid var(--border);
  }
  .pm-logo-name {
    font-family: Georgia, serif; font-size: 20px; font-weight: 500; display: block;
  }
  .pm-logo-sub {
    font-size: 10px; color: var(--text-soft); letter-spacing: 0.5px; text-transform: uppercase; display: block; margin-top: -1px;
  }
  .pm-header-right { display: flex; align-items: center; gap: 10px; }
  .pm-lang-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 20px;
    border: 0.5px solid var(--border); background: var(--surface);
    font-size: 13px; cursor: pointer; font-family: inherit; color: var(--text);
  }
  .pm-lang-name { display: inline; }

  .pm-lang-dropdown {
    position: fixed; top: 56px; right: 16px; z-index: 50;
    background: var(--surface); border: 0.5px solid var(--border);
    border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.12);
    padding: 6px; min-width: 180px; max-height: 360px; overflow-y: auto;
  }
  .pm-lang-option {
    display: flex; align-items: center; gap: 8px; width: 100%;
    padding: 8px 12px; border: none; background: none; border-radius: 8px;
    font-size: 14px; cursor: pointer; font-family: inherit; color: var(--text);
    text-align: left;
  }
  .pm-lang-option:hover { background: #f5f2ed; }
  .pm-lang-option.active { background: #fde8e7; color: var(--accent); font-weight: 500; }

  .pm-hero {
    max-width: 960px; margin: 0 auto;
    padding: 40px 20px 24px;
    text-align: center;
  }
  .pm-hero h1 {
    font-family: Georgia, serif; font-size: 36px; font-weight: 500;
    letter-spacing: -0.5px; margin-bottom: 8px;
  }
  .pm-hero p {
    font-size: 15px; color: var(--text-muted); max-width: 460px; margin: 0 auto;
  }

  .pm-search-wrap {
    max-width: 960px; margin: 0 auto;
    padding: 0 20px 16px; position: relative;
  }
  .pm-search {
    width: 100%; padding: 12px 40px 12px 42px;
    border: 0.5px solid var(--border); border-radius: 14px;
    background: var(--surface); font-size: 15px; outline: none;
    font-family: inherit; color: var(--text);
  }
  .pm-search:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(218,37,29,0.08); }
  .pm-search-icon {
    position: absolute; left: 34px; top: 22px;
    color: var(--text-soft); pointer-events: none;
  }
  .pm-search-clear {
    position: absolute; right: 30px; top: 14px;
    width: 24px; height: 24px; border-radius: 50%;
    border: none; background: #eee7dd; color: var(--text-muted);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; padding: 0;
  }
  .pm-search-clear:hover { background: var(--text); color: white; }

  /* Suggestions dropdown */
  .pm-suggest {
    position: absolute; left: 20px; right: 20px; top: calc(100% - 4px);
    background: var(--surface); border: 0.5px solid var(--border);
    border-radius: 14px; box-shadow: 0 12px 40px rgba(0,0,0,0.12);
    padding: 10px; z-index: 30;
    animation: pm-fade-in 0.15s ease;
    max-height: 420px; overflow-y: auto;
  }
  .pm-suggest-section { padding: 6px 4px; }
  .pm-suggest-section + .pm-suggest-section { border-top: 0.5px solid var(--border); margin-top: 4px; padding-top: 10px; }
  .pm-suggest-title {
    font-size: 10px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.08em; color: var(--text-soft);
    padding: 0 8px 8px;
  }
  .pm-suggest-chips {
    display: flex; flex-wrap: wrap; gap: 6px; padding: 0 4px 4px;
  }
  .pm-suggest-chip {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 12px; border-radius: 20px;
    border: 0.5px solid var(--border); background: #faf8f5;
    font-family: inherit; font-size: 13px; color: var(--text);
    cursor: pointer; transition: all 0.15s;
  }
  .pm-suggest-chip:hover { background: var(--text); color: white; border-color: var(--text); }
  .pm-suggest-chip-count {
    font-size: 11px; color: var(--text-soft); font-weight: 600;
  }
  .pm-suggest-chip:hover .pm-suggest-chip-count { color: rgba(255,255,255,0.7); }
  .pm-suggest-chip-dym { background: #fff7e6; border-color: #f0d090; color: #7a5a00; }
  .pm-suggest-chip-dym:hover { background: var(--accent); color: white; border-color: var(--accent); }

  .pm-suggest-list { list-style: none; padding: 0; margin: 0; }
  .pm-suggest-item {
    width: 100%; display: flex; align-items: center; gap: 10px;
    padding: 10px 10px; border-radius: 10px;
    border: none; background: transparent; cursor: pointer;
    font-family: inherit; color: var(--text); text-align: left;
    transition: background 0.12s;
  }
  .pm-suggest-item:hover, .pm-suggest-item:focus { background: #f5f2ed; outline: none; }
  .pm-suggest-item-icon {
    width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
    background: #fdece9; color: var(--accent);
    display: flex; align-items: center; justify-content: center;
  }
  .pm-suggest-item-text {
    flex: 1; font-size: 14px; color: var(--text);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .pm-suggest-item-text mark {
    background: rgba(218,37,29,0.12); color: var(--accent);
    padding: 0; font-weight: 700; border-radius: 2px;
  }
  .pm-suggest-item-cat {
    font-size: 11px; color: var(--text-soft);
    background: #f3efe6; padding: 3px 8px; border-radius: 10px;
    flex-shrink: 0;
  }
  .pm-suggest-empty {
    padding: 12px 8px; font-size: 13px; color: var(--text-muted);
  }

  .pm-cats-wrap {
    max-width: 960px; margin: 0 auto;
    padding: 0 20px 20px;
    overflow-x: auto; -webkit-overflow-scrolling: touch;
  }
  .pm-cats {
    display: flex; gap: 6px; min-width: max-content;
  }
  .pm-cat {
    padding: 7px 16px; border-radius: 20px;
    border: 0.5px solid var(--border); background: transparent;
    font-size: 13px; color: var(--text-muted); cursor: pointer;
    font-family: inherit; white-space: nowrap; transition: all 0.15s;
  }
  .pm-cat:hover { border-color: var(--text); color: var(--text); }
  .pm-cat.active { background: var(--text); color: white; border-color: var(--text); }

  .pm-main {
    max-width: 960px; margin: 0 auto;
    padding: 0 20px 60px;
  }
  .pm-section { margin-bottom: 36px; }
  .pm-section-title {
    font-family: Georgia, serif; font-size: 22px; font-weight: 500;
    margin-bottom: 16px; display: flex; align-items: center; gap: 10px;
    letter-spacing: -0.3px;
  }
  .pm-section-count {
    font-family: -apple-system, sans-serif; font-size: 12px; font-weight: 500;
    background: #f3efe6; color: var(--text-muted); padding: 2px 8px;
    border-radius: 10px;
  }

  .pm-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 14px;
  }
  .pm-card {
    background: var(--surface); border: 0.5px solid var(--border);
    border-radius: 14px; overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .pm-card:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.06); }
  .pm-card-img {
    width: 100%; height: 140px; overflow: hidden; background: #f5f2ed;
  }
  .pm-card-img img {
    width: 100%; height: 100%; object-fit: cover;
    transition: transform 0.3s;
  }
  .pm-card:hover .pm-card-img img { transform: scale(1.05); }
  .pm-card-img-placeholder {
    display: flex; align-items: center; justify-content: center;
  }
  .pm-card-img-placeholder span {
    font-family: Georgia, serif; font-size: 36px; color: #e0dbd4; font-weight: 500;
  }
  .pm-card-body { padding: 14px; }
  .pm-card-body h3 {
    font-size: 15px; font-weight: 500; line-height: 1.3; margin: 0;
  }
  .pm-card-body p {
    font-size: 12px; color: var(--text-muted); margin-top: 4px; line-height: 1.4;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }

  .pm-empty {
    text-align: center; padding: 60px 20px;
    color: var(--text-soft); font-size: 16px;
  }
  .pm-empty-title { font-size: 16px; color: var(--text); margin-bottom: 16px; }
  .pm-empty-title strong { color: var(--accent); font-weight: 600; }
  .pm-empty-suggest {
    display: flex; flex-wrap: wrap; gap: 8px; justify-content: center;
    align-items: center; margin-bottom: 16px;
  }
  .pm-empty-suggest-label {
    font-size: 13px; color: var(--text-muted); margin-right: 4px;
  }
  .pm-empty-clear {
    display: inline-block; padding: 8px 18px; border-radius: 20px;
    border: 0.5px solid var(--border); background: var(--surface);
    font-family: inherit; font-size: 13px; color: var(--text);
    cursor: pointer; transition: all 0.15s;
  }
  .pm-empty-clear:hover { background: var(--text); color: white; border-color: var(--text); }

  .pm-footer {
    border-top: 0.5px solid var(--border); background: var(--surface);
    text-align: center; padding: 24px 20px;
  }
  .pm-footer p { font-size: 13px; color: var(--text-muted); }
  .pm-footer-rcs { margin-top: 6px; font-size: 12px; color: var(--text-soft); }
  .pm-footer-rcs a { color: var(--accent); font-weight: 500; }

  /* Dish Detail Modal */
  .pm-modal-overlay {
    position: fixed; inset: 0; z-index: 100;
    background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    backdrop-filter: blur(4px);
    animation: pm-fade-in 0.2s ease;
  }
  @keyframes pm-fade-in { from { opacity: 0; } to { opacity: 1; } }
  .pm-modal {
    background: var(--surface);
    border-radius: 24px;
    overflow: hidden;
    max-width: 420px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 25px 60px rgba(0,0,0,0.3);
    animation: pm-slide-up 0.25s ease;
  }
  @keyframes pm-slide-up { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
  .pm-modal-close {
    position: absolute; top: 12px; right: 12px; z-index: 10;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(0,0,0,0.5); border: none;
    color: white; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    backdrop-filter: blur(8px);
  }
  .pm-modal-close:hover { background: rgba(0,0,0,0.7); }
  .pm-modal-img {
    width: 100%; height: 220px; overflow: hidden;
  }
  .pm-modal-img img {
    width: 100%; height: 100%; object-fit: cover;
  }
  .pm-modal-img.pm-modal-img-ph {
    background: linear-gradient(135deg, #da251d, #ff6b4a);
    display: flex; align-items: center; justify-content: center;
  }
  .pm-modal-img-ph span {
    font-size: 64px; font-weight: 800; color: rgba(255,255,255,0.3);
  }
  .pm-modal-body { padding: 20px 24px 24px; }
  .pm-modal-cat {
    font-size: 11px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.1em; color: var(--accent);
    display: inline-block; margin-bottom: 6px;
  }
  .pm-modal-name {
    font-size: 22px; font-weight: 800; color: var(--text);
    line-height: 1.2; margin-bottom: 8px;
  }
  .pm-modal-desc {
    font-size: 14px; color: var(--text-muted); line-height: 1.5;
    margin-bottom: 16px;
  }
  .pm-modal-section {
    background: #f8f6f3; border-radius: 14px; padding: 14px 16px;
    margin-bottom: 16px;
  }
  .pm-modal-label {
    font-size: 12px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.08em; color: var(--text-muted);
    display: flex; align-items: center; gap: 6px; margin-bottom: 8px;
  }
  .pm-modal-ingredients {
    font-size: 14px; color: var(--text); line-height: 1.6;
  }
  .pm-modal-actions {
    display: flex; flex-direction: column; gap: 10px;
  }
  .pm-modal-btn {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    padding: 14px 20px; border-radius: 14px; font-size: 14px;
    font-weight: 700; cursor: pointer; font-family: inherit;
    text-decoration: none; transition: all 0.15s;
    border: none;
  }
  .pm-modal-btn-nav {
    background: var(--accent); color: white;
  }
  .pm-modal-btn-nav:hover { background: #b91c14; }

  .pm-card { cursor: pointer; transition: transform 0.15s, box-shadow 0.15s; }
  .pm-card:active { transform: scale(0.97); }

  /* Reviews Banner */
  .pm-reviews-bar {
    max-width: 960px; margin: 0 auto;
    padding: 0 20px 16px;
    display: flex; align-items: center; justify-content: center;
    gap: 16px;
  }
  .pm-review-badge {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px; border-radius: 14px;
    background: var(--surface); border: 0.5px solid var(--border);
    text-decoration: none; color: var(--text);
    transition: box-shadow 0.15s, transform 0.15s;
  }
  .pm-review-badge:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.08); transform: translateY(-1px); }
  .pm-review-info { display: flex; flex-direction: column; }
  .pm-review-stars { display: flex; align-items: center; gap: 4px; }
  .pm-stars { display: flex; gap: 1px; }
  .pm-review-rating { font-size: 14px; font-weight: 800; color: var(--text); }
  .pm-review-count { font-size: 11px; color: var(--text-soft); }
  .pm-review-sep { width: 1px; height: 36px; background: var(--border); }

  @media (max-width: 640px) {
    .pm-hero { padding: 28px 16px 16px; }
    .pm-hero h1 { font-size: 28px; }
    .pm-hero p { font-size: 14px; }
    .pm-search-wrap { padding: 0 16px 12px; }
    .pm-search-icon { left: 30px; }
    .pm-search-clear { right: 26px; }
    .pm-suggest { left: 16px; right: 16px; }
    .pm-cats-wrap { padding: 0 16px 16px; }
    .pm-main { padding: 0 16px 40px; }
    .pm-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
    .pm-card-img { height: 110px; }
    .pm-card-body { padding: 10px; }
    .pm-card-body h3 { font-size: 13px; }
    .pm-section-title { font-size: 19px; }
    .pm-header-inner { padding: 12px 16px; }
    .pm-logo-name { font-size: 17px; }
    .pm-lang-name { display: none; }
  }
`;

export default PublicMenu;
